// File: src/Subprogram/Products.jsx
import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import * as Icons from 'lucide-react'; // دریافت تمام آیکون‌ها برای رندر داینامیک
import { 
  Search, Download, Box, Crown, Loader2,
  Menu, ChevronDown, ShoppingCart
} from 'lucide-react';

const API_BASE = 'https://free3dmax.ir/api';

export const formatPrice = (price) => {
  return new Intl.NumberFormat('fa-IR').format(price) + ' تومان';
};

const AssetCard = ({ asset, onClick, categories }) => {
  // پیدا کردن نام فارسی دسته‌بندی از روی لیست داینامیک
  const categoryLabel = categories.find(c => c.slug === asset.category)?.label || asset.category;

  return (
    <div 
        onClick={() => onClick(asset)}
        className="group bg-white rounded-[2rem] p-3 shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100 cursor-pointer flex flex-col h-full relative"
    >
        <div className="relative aspect-[4/3] rounded-[1.5rem] overflow-hidden bg-slate-100 mb-4">
            <img 
                src={asset.image} 
                alt={asset.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                onError={(e) => {
                  e.target.onerror = null; 
                  e.target.src = "https://via.placeholder.com/400x300?text=No+Preview"; 
                }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            
            {!asset.is_free ? (
                <div className="absolute top-3 right-3 z-10 bg-gradient-to-r from-amber-300 via-amber-400 to-orange-500 text-white text-xs font-black px-3 py-1.5 rounded-full shadow-[0_4px_10px_rgba(251,191,36,0.5)] flex items-center gap-1 border border-white/20 transform group-hover:scale-105 transition-transform">
                    <Crown size={14} fill="white" />
                    <span className="tracking-wider drop-shadow-sm">VIP</span>
                </div>
            ) : (
                <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                    <Download size={18} className="text-blue-600" />
                </div>
            )}
            
            <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-md text-white text-[10px] px-2 py-1 rounded-lg">
                {asset.renderer}
            </div>
        </div>

        <div className="px-2 pb-2 flex flex-col flex-grow">
            <div className="flex justify-between items-start mb-2">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider truncate max-w-[120px]">
                    {categoryLabel}
                </span>
                <span className="text-[10px] text-slate-400 bg-slate-50 px-2 py-0.5 rounded-md border border-slate-100 whitespace-nowrap">{asset.file_size}</span>
            </div>
            
            <h3 className="font-bold text-slate-800 text-sm mb-3 line-clamp-1 group-hover:text-blue-600 transition-colors">
                {asset.title}
            </h3>

            <div className="mt-auto flex items-center justify-between pt-3 border-t border-slate-50">
                <div className="flex items-center gap-1.5">
                    {asset.is_free ? (
                        <span className="text-emerald-500 font-black text-sm">رایگان</span>
                    ) : (
                        <div className="flex flex-col items-end">
                            <span className="text-slate-800 font-black text-sm">
                                {new Intl.NumberFormat('fa-IR').format(asset.price)}
                            </span>
                        </div>
                    )}
                    {!asset.is_free && <span className="text-[10px] text-slate-400">تومان</span>}
                </div>
                
                <button className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${asset.is_free ? 'bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white' : 'bg-orange-50 text-orange-600 group-hover:bg-orange-500 group-hover:text-white'}`}>
                    {asset.is_free ? <Download size={16} /> : <ShoppingCart size={16} />}
                </button>
            </div>
        </div>
    </div>
  );
};

const MainContent = ({ categories, onProductSelect, selectedCategory, setSelectedCategory, searchQuery, setSearchQuery }) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnlyVIP, setShowOnlyVIP] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
        try {
            const response = await axios.get(`${API_BASE}/products.php`);
            if (Array.isArray(response.data)) {
                setAssets(response.data);
            }
        } catch (error) {
            console.error("Failed to load products", error);
        } finally {
            setLoading(false);
        }
    };
    fetchProducts();
  }, []);

  const filteredAssets = useMemo(() => {
    return assets.filter(asset => {
      const matchesCategory = selectedCategory === 'all' || asset.category === selectedCategory;
      const matchesSearch = asset.title.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesVIP = showOnlyVIP ? !asset.is_free : true;
      return matchesCategory && matchesSearch && matchesVIP;
    });
  }, [selectedCategory, searchQuery, showOnlyVIP, assets]);

  return (
    <section id="main-content" className="bg-slate-50 min-h-screen pt-4 pb-24">
        <div className="sticky top-28 lg:top-[160px] z-40 bg-slate-50/95 backdrop-blur-md border-b border-slate-200 shadow-sm py-4 transition-all mb-12">
            <div className="container mx-auto px-4">
                <div className="flex flex-col gap-4">
                    
                    {/* جستجو و منوی موبایل (داینامیک شده) */}
                    <div className="flex flex-col lg:hidden gap-4 relative z-50">
                        <div className="relative w-full">
                            <button 
                                onClick={() => setIsMenuOpen(!isMenuOpen)} 
                                className="flex items-center justify-between w-full px-5 py-3.5 bg-[#E6E6FA] text-slate-800 rounded-2xl font-bold shadow-sm transition-all hover:bg-[#cfcfcf]"
                            >
                                <div className="flex items-center gap-3">
                                    <Menu size={20} className="text-blue-600" />
                                    <span>دسته‌بندی محصولات</span>
                                </div>
                                <ChevronDown size={16} className={`transition-transform duration-300 ${isMenuOpen ? 'rotate-180' : ''}`} />
                            </button>

                            <div className={`absolute top-full right-0 mt-3 w-full bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden transition-all duration-300 origin-top ${isMenuOpen ? 'opacity-100 scale-100 visible' : 'opacity-0 scale-95 invisible'}`}>
                                <div className="flex flex-col max-h-[60vh]">
                                    <div className="w-full bg-slate-50 overflow-y-auto flex-1 min-h-0 py-4 custom-scrollbar block">
                                        <button 
                                            onClick={() => { setSelectedCategory('all'); setIsMenuOpen(false); }}
                                            className={`flex items-center gap-3 px-6 py-3 cursor-pointer text-sm font-bold transition-all w-full text-right ${selectedCategory === 'all' ? 'text-blue-600 bg-white' : 'text-slate-600 hover:text-blue-600'}`}
                                        >
                                            <Icons.Grid size={18} className={selectedCategory === 'all' ? 'text-blue-500' : 'text-slate-400'}/>
                                            همه محصولات
                                        </button>
                                        <div className="h-px bg-slate-200 mx-4 my-2"></div>
                                        
                                        {categories.map(cat => {
                                            const DynamicIcon = Icons[cat.icon] || Icons.Box;
                                            return (
                                                <button 
                                                    key={cat.id}
                                                    onClick={() => { setSelectedCategory(cat.slug); setIsMenuOpen(false); }}
                                                    className={`flex items-center gap-3 px-6 py-3 cursor-pointer text-sm font-bold transition-all w-full text-right ${selectedCategory === cat.slug ? 'text-blue-600 bg-white' : 'text-slate-600 hover:bg-slate-100/50'}`}
                                                >
                                                    <DynamicIcon size={18} className={selectedCategory === cat.slug ? 'text-blue-500' : 'text-slate-400'} />
                                                    {cat.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="w-full relative group shrink-0">
                            <input 
                                type="text" 
                                placeholder="جستجو در محصولات..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full bg-white border-2 border-slate-100 rounded-2xl pl-12 pr-4 py-3.5 text-sm font-bold focus:outline-none focus:border-blue-500 transition-all text-slate-700 placeholder:text-slate-400"
                            />
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" size={20} />
                        </div>
                    </div>

                    <div className="flex items-center justify-center lg:justify-start">
                        <div className="bg-white p-1 rounded-2xl shadow-sm border border-slate-200 inline-flex relative z-0">
                            <button
                                onClick={() => setShowOnlyVIP(false)}
                                className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${!showOnlyVIP ? 'bg-slate-800 text-white shadow-md' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}
                            >
                                همه محصولات
                            </button>
                            <button
                                onClick={() => setShowOnlyVIP(true)}
                                className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${showOnlyVIP ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-md shadow-orange-500/30' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}
                            >
                                <Crown size={16} />
                                فقط VIP
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        </div>

        <div className="container mx-auto px-4 animate-fade-in-up">
            <div className="flex items-center gap-3 mb-8">
                <div className="w-1 h-8 bg-blue-600 rounded-full"></div>
                <h2 className="text-2xl font-black text-slate-800">
                    {selectedCategory === 'all' 
                      ? 'همه محصولات' 
                      : categories.find(c => c.slug === selectedCategory)?.label || selectedCategory}
                </h2>
                <span className="text-sm text-slate-400 bg-white px-3 py-1 rounded-full border border-slate-200 shadow-sm">
                    {filteredAssets.length} فایل
                </span>
            </div>

            {loading ? (
                 <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-600" size={40}/></div>
            ) : filteredAssets.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                    {filteredAssets.map(asset => (
                        <AssetCard 
                            key={asset.id} 
                            asset={asset} 
                            onClick={onProductSelect}
                            categories={categories}
                        />
                    ))}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-dashed border-slate-300">
                    <Box size={48} className="text-slate-300 mb-4" />
                    <h3 className="text-xl font-bold text-slate-600 mb-2">محصولی یافت نشد</h3>
                </div>
            )}
        </div>
    </section>
  );
};

export default MainContent;